/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/
import {
  AfterContentChecked,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnInit,
  QueryList,
  SimpleChanges,
  ViewChildren,
  ChangeDetectionStrategy
} from '@angular/core';
import {StatusTimelineConfigurationModel} from './models/status-timeline-configuration.model';
import {HistoryStateConfigurationModel} from './models/history-state-configuration.model';
import {StateConfigurationModel} from './models/state-configuration.model';
import {StatefulComponent} from '../stateful/stateful.component';
import {StatusTimelineState} from './models/status-timeline.state';
import {HistoryStateDisplayConfigurationModel} from './models/history-state-display-configuration.model';
import STATUS_TIMELINE_CONSTANTS from './models/status-timeline-constants';

@Component({
    selector: 'app-status-timeline',
    templateUrl: './status-timeline.component.html',
    styleUrls: ['./status-timeline.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class StatusTimelineComponent extends StatefulComponent<StatusTimelineState> implements OnInit, OnChanges, AfterContentChecked {
    @ViewChildren('historyLineEntry') historyLineEntries: QueryList<ElementRef>;
    @Input() configuration: StatusTimelineConfigurationModel;
    readonly collapsibleButtonCypressTag = 'collapsible-button';

    constructor(private cdr: ChangeDetectorRef) {
        super();
    }

    ngOnInit(): void {
        // retrieveTimelineLevels needs to be called first because it calculates the full timeline size, in order
        // to populate the state.expandedLevelsSize
        this.storeState({levels: this.retrieveTimelineLevels(), isCollapsed: false});

        if (this.hasCollapsibleButtonWhenExpanded()) {
            this.toggleCollapse(true);
        }
    }

    ngAfterContentChecked(): void {
        this.cdr.detectChanges();
    }

    ngOnChanges(changes: SimpleChanges): void {
        // Render the timeline only when effectively occurred a change on the configuration
        if (JSON.stringify(changes['configuration'].previousValue) !== JSON.stringify(changes['configuration'].currentValue)) {
            if (this.state.isCollapsed) {
                this.storeState({levels: this.retrieveSummaryTimelineLevel(), isCollapsed: this.state.isCollapsed});
            } else {
                this.storeState({levels: this.retrieveTimelineLevels(), isCollapsed: this.state.isCollapsed});
            }
        }
    }

    stateDotCypressTag(levelIndex: number, index: number): string {
        return `${this.baseStateCypressTag(levelIndex, index)}-dot`;
    }

    stateNameCypressTag(levelIndex: number, index: number): string {
        return `${this.baseStateCypressTag(levelIndex, index)}-name`;
    }

    stateDateCypressTag(levelIndex: number, index: number): string {
        return `${this.baseStateCypressTag(levelIndex, index)}-date`;
    }

    stateFlagCypressTag(levelIndex: number, index: number): string {
        return `${this.baseStateCypressTag(levelIndex, index)}-flag`;
    }

    timelineWrapperStyle(): { [key: string]: string } {
        return {
            ...this.backgroundStyle(),
            // default width is 100%
            'width': `${this.configuration.widthPercentage ?? 100}%`
        };
    }

    backgroundStyle(): { [key: string]: string } {
        return {
            'background-color': this.configuration.backgroundColor
        };
    }

    showCollapseArrow(entry: HistoryStateDisplayConfigurationModel): boolean {
        return entry.collapsible && this.retrieveTimelineLevels().length > 1;
    }

    labelClass(baseClass: string,
               level: HistoryStateDisplayConfigurationModel[],
               levelIndex: number,
               entry: HistoryStateDisplayConfigurationModel): string {
        let composedClass = baseClass;
        const filledLevelPositions = level.filter((item) => item.state.name != null);

        // With 2 levels we don't have a collapsible button
        if (this.state.levels.length > 1 && levelIndex < this.state.levels.length - 1) {
            const entryIndex = filledLevelPositions.indexOf(entry);
            if (entryIndex === 0) {
                composedClass += ' label-left-of-dot';
            } else if (entryIndex === filledLevelPositions.length - 1) {
                composedClass += ' label-right-of-dot';
            }
        }
        return composedClass;
    }

    /**
     * Returns if a given level is the last one
     * @param levelIndex the index of the level
     */
    isLastLevel(levelIndex: number): boolean {
        const levels = this.state.levels;
        return levels.length - 1 === levelIndex;
    }

    getLabelName(entry: HistoryStateDisplayConfigurationModel): string {
        let label;
        if (entry.state.count == null) {
            label = `${entry.state?.name}`;
        } else if (entry.state.nextStatus != null) {
            label = `${entry.state?.name} ${entry.state?.count} ${entry.state.nextStatus}`;
        } else {
            label = `${entry.state?.name} ${entry.state?.count}`;
        }
        return label;
    }

    /**
     * Returns the correct grid-template for a given level of the timeline
     * @param level the {@link HistoryStateConfigurationModel[]} of the level
     */
    retrieveGridTemplateForLevel(level: HistoryStateConfigurationModel[]): { [key: string]: string } {
        const levels = this.state.levels;

        // The last line will always have the full length of items
        const gridItemWidth = 100 / levels[levels.length - 1].length;

        return {
            'grid-template-columns': `repeat(${level.length}, ${gridItemWidth}%)`
        };
    }

    /**
     * Returns a given entry timestamp
     */
    getEntryTimestamp(entry: HistoryStateConfigurationModel): number {
        let timestamp: number = entry.timestamp;
        if (entry.timestamp == null) {
            timestamp = Date.now();
        }
        return timestamp;
    }

    /**
     * Return the css style to apply to a given level horizontal line
     * @param levelIndex the level index
     */
    historyLineStyle(levelIndex: number): { [key: string]: string } {
        const levels = this.state.levels;
        if (this.historyLineEntries != null && levelIndex < levels.length) {
            // All the levels into a flat array
            const flatLevels = levels.flat();
            // Filter the level entries that are not empty and not a prediction (so we know which are the populated 1st and last entry to
            // look for in the flatLevels)
            const filledLevelPositions = levels[levelIndex]?.filter((entry) => entry.state.name != null && !entry.prediction);

            const firstPositionIndex = flatLevels.indexOf(filledLevelPositions[0]);
            const lastPositionIndex = flatLevels.indexOf(filledLevelPositions[filledLevelPositions.length - 1]);
            const firstPosition = this.getLineEntryClientRect(firstPositionIndex, this.historyLineEntries);
            const lastPosition = this.getLineEntryClientRect(lastPositionIndex, this.historyLineEntries);

            if (firstPosition != null && lastPosition != null) {
                const emptyPositionsBeforeFirstPosition = levels[levelIndex].indexOf(filledLevelPositions[0]);

                // If is the first level, the line has to be positioned at half the width of the first element
                // Otherwise, it has to be positioned at width * how many positions exist before + half the width
                // All the timeline elements have the same width
                const left = levelIndex === 0 ? (firstPosition.width / 2)
                        : (firstPosition.width * emptyPositionsBeforeFirstPosition + firstPosition.width / 2);

                // 11 is half of the dot wrapper height (21px) + height of the line (1px) -> (21 + 1)/2 = 11
                return {
                    'top': `11px`,
                    'left': `${left}px`,
                    'width': `${lastPosition.x - firstPosition.x}px`
                };
            }
        }
    }

    /**
     * Return the css style to apply to a given level diagonalLineStyle line
     * @param levelIndex the level index
     */
    diagonalLineStyle(levelIndex: number): { [key: string]: string } {
        const levels = this.state.levels;
        if (this.historyLineEntries != null && levelIndex < levels.length) {
            // All the levels into a flat array
            const flatLevels = levels.flat();
            // Filter the level entries that are not empty (so we know which are the populated 1st and last entry to look for in the
            // flatLevels)
            const filledLevelPositions = levels[levelIndex].filter((entry) => entry.state.name != null);

            const repeatablePosition = filledLevelPositions.find((entry) => entry.state.repeatable);
            const lastNonRepeatablePosition = filledLevelPositions[filledLevelPositions.indexOf(repeatablePosition) - 1];

            // The firstPositionIndex will always be the lastNonRepeatablePosition index within the flatLevels (exception: first level with
            // only 2 entries) If the first level has only 2 entries (i.e.: filledLevelPositions.length = 2), the firstPositionIndex within
            // the historyLineEntries matches the added empty state (i.e.: [first, empty, last])
            const firstPositionIndex = (levelIndex === 0 && filledLevelPositions.length === 2) ? 1
                    : flatLevels.indexOf(lastNonRepeatablePosition);
            const lastPositionIndex = flatLevels.indexOf(filledLevelPositions[filledLevelPositions.length - 1]);

            const firstPosition = this.getLineEntryClientRect(firstPositionIndex, this.historyLineEntries);

            if (firstPosition != null) {
                // The diagonal line corresponds to the hypotenuse of a right triangle so its length and rotation may be calculated using
                // the pythagorean theorem
                const adjacentLeg = (lastPositionIndex - firstPositionIndex) * firstPosition.width;
                // 18 is the padding-bottom of the line
                const oppositeLeg = 18 + firstPosition.height;
                const hypotenuse = Math.hypot(oppositeLeg, adjacentLeg);
                const sineOfRotationAngle = oppositeLeg / hypotenuse;
                const rotationAngleInRads = Math.PI - Math.asin(sineOfRotationAngle);

                return {
                    'width': `${hypotenuse}px`,
                    'transform': `rotate(${rotationAngleInRads}rad)`
                };
            }
        }

    }

    predictionLineStyle(levelIndex: number): { [key: string]: string } {
        const levels = this.state.levels;
        if (this.historyLineEntries != null && levelIndex < levels.length) {
            // All the levels into a flat array
            const flatLevels = levels.flat();
            const predictionLevelPositions = flatLevels.filter((entry) => entry.prediction);
            const filledLevelPositions = flatLevels.filter((entry) => entry.state.name != null && !entry.prediction);

            const lastHistoryPositionIndex = flatLevels.indexOf(
                    flatLevels.find((entry) => entry === filledLevelPositions[filledLevelPositions.length - 1]));
            const lastPredictionPositionIndex = flatLevels.indexOf(
                    flatLevels.find((entry) => entry === predictionLevelPositions[predictionLevelPositions.length - 1]));

            const lastHistoryPosition = this.getLineEntryClientRect(lastHistoryPositionIndex, this.historyLineEntries);
            const lastPredictionPosition = this.getLineEntryClientRect(lastPredictionPositionIndex, this.historyLineEntries);

            if (lastHistoryPosition != null && lastPredictionPosition != null) {
                const positionsBeforePrediction = levels[levelIndex].indexOf(filledLevelPositions[filledLevelPositions.length - 1]);

                const left = (lastHistoryPosition.width * (positionsBeforePrediction) + lastHistoryPosition.width / 2);
                const width = lastPredictionPosition.x - lastHistoryPosition.x;

                // top -> 11px is half of the dot wrapper height (21px) + line width (1px)
                return {
                    'top': '12px',
                    'left': `${left}px`,
                    'width': `${width}px`
                };
            }
        }
    }

    entryClass(entry: HistoryStateDisplayConfigurationModel): string {
        let entryClass = 'status-timeline-entry';
        if (entry.prediction) {
            entryClass += ' prediction';
        }
        if (!this.state.isCollapsed) {
            entryClass += ' uncollapsed';
        }
        return entryClass;
    }

    /**
     * Returns the class to be applied to a dot of a given entry
     * @param entry the {@link StateConfigurationModel} from the entry
     */
    entryDotClass(entry: StateConfigurationModel): string {
        let dotClass = 'status-timeline-entry-dot intermediary-entry';
        if (entry.initialState) {
            dotClass = 'status-timeline-entry-dot initial-entry';
        }

        if (entry.finalState && !entry.intermediateState) {
            dotClass = 'status-timeline-entry-dot final-entry';
        }

        if (this.isDotAtCurrentState(entry)) {
            dotClass = 'status-timeline-entry-dot current-entry';
        }

        return dotClass;
    }

    /**
     * Returns the css inline style to be applied to a given entry
     * @param entry the {@link HistoryStateDisplayConfigurationModel} from the entry
     */
    entryDotStyle(entry: HistoryStateDisplayConfigurationModel): { [key: string]: string } {
        let style: { [key: string]: string };

        if (entry.state.initialState) {
            style = {
                'background-color': STATUS_TIMELINE_CONSTANTS.colors.fixedPath.backgroundColor,
                'border-color': STATUS_TIMELINE_CONSTANTS.colors.fixedPath.borderColor
            };
        } else if (this.isDotAtCurrentState(entry.state)) {
            const color = entry.state.color ?? STATUS_TIMELINE_CONSTANTS.colors.fixedPath.borderColor;
            style = {
                'outline': `1px solid ${color}`,
                'outline-offset': '-3px'
            };
        } else if (entry.prediction) {
            style = {
                'background-color': STATUS_TIMELINE_CONSTANTS.colors.predictionPath.backgroundColor,
                'border-color': STATUS_TIMELINE_CONSTANTS.colors.predictionPath.borderColor
            };
        } else {
            // Condition for the past states and final state
            const backgroundColor = entry.state.color ?? STATUS_TIMELINE_CONSTANTS.colors.fixedPath.backgroundColor;
            const borderColor = entry.state.color ?? STATUS_TIMELINE_CONSTANTS.colors.fixedPath.borderColor;
            style = {
                'background-color': backgroundColor,
                'border': `1px solid ${borderColor}`
            };
        }

        return style;
    }

    collapseArrowClass(): string {
        let arrowClass = 'state-collapse-arrow';
        this.state.isCollapsed ? arrowClass += ' iwp-icon-gen_arrow_right' : arrowClass += ' iwp-icon-gen_arrow_down uncollapsed';
        return arrowClass;
    }

    retrieveRemainingHappyPath(): StateConfigurationModel[] {
        const finalHistoryState = this.configuration.statusHistory[this.configuration.statusHistory.length - 1];

        let happyPathCutState = this.configuration.happyPath.find((item) => item.code === finalHistoryState.state?.code);
        let cutIndex = this.configuration.happyPath.indexOf(happyPathCutState) + 1;

        if (happyPathCutState == null) {
            happyPathCutState = this.configuration.happyPath.find((item) => item.code === finalHistoryState.nextStateCode);
            cutIndex = this.configuration.happyPath.indexOf(happyPathCutState);
        }

        return this.configuration.happyPath.slice(cutIndex);

    }

    toggleCollapse(changeState: boolean = true): void {
        if (changeState) {
            this.storeState({isCollapsed: !this.state.isCollapsed});
        }

        if (this.state.isCollapsed) {
            this.storeState({levels: this.retrieveSummaryTimelineLevel()});
        } else {
            this.storeState({levels: this.retrieveTimelineLevels()});
        }
    }

    /**
     * Returns if the last element {@link configuration.statusHistory} is final state
     */
    isCurrentStateFinal(): boolean {
        return [...(this.configuration?.statusHistory ?? [])].pop()?.state?.finalState;
    }

    /**
     * Returns the {@link DOMRect} of an {@link ElementRef} within a {@link QueryList}
     * @param index the index of the {@link ElementRef}
     * @param queryList the {@link QueryList} to each the {@link ElementRef} belongs
     */
    private getLineEntryClientRect(index: number, queryList: QueryList<ElementRef>): DOMRect {
        const element: ElementRef = queryList.find((item, i) => i === index);
        return element?.nativeElement?.getBoundingClientRect();
    }

    /**
     * Retrieves the collapsed timeline summary
     * @returns HistoryStateConfigurationModel[][] with one line with the summary information
     */
    private retrieveSummaryTimelineLevel(): HistoryStateDisplayConfigurationModel[][] {
        let summaryArray = this.state.levels;

        if (this.state.levels.length > 1) {
            let lastLevelSection: HistoryStateDisplayConfigurationModel[];
            let firstLevelSection: HistoryStateDisplayConfigurationModel[];

            // Get states until collapsible button
            const firstLevel = [...this.state.levels].shift();
            const lastLevel = [...this.state.levels].pop();
            const collapsibleButtonStateIndex = firstLevel
                    .indexOf(firstLevel.find((entry) => entry.state.repeatable));
            const beforeCollapsibleButtonStates = firstLevel.slice(0, collapsibleButtonStateIndex);

            if (this.configuration.showAllStatesOnLastLine) {
                // Get states from penultimate state after collapsible button
                const penultimateLevel = [...this.state.levels[this.state.levels.length - 2]];
                const AfterCollapsibleButtonStates = penultimateLevel.slice(collapsibleButtonStateIndex);

                firstLevelSection = beforeCollapsibleButtonStates.concat(AfterCollapsibleButtonStates);
                lastLevelSection = [...this.state.levels].pop();

                summaryArray = [
                    firstLevelSection,
                    lastLevelSection
                ];
            } else {
                lastLevelSection = lastLevel.slice(collapsibleButtonStateIndex);

                summaryArray = [beforeCollapsibleButtonStates.concat(lastLevelSection)];
            }
        }
        return summaryArray;
    }

    /**
     * Retrieves the timeline levels (rows) from the input {@link configuration}
     * @returns HistoryStateConfigurationModel[][] where each array entry matches one of the lines
     */
    private retrieveTimelineLevels(): HistoryStateDisplayConfigurationModel[][] {
        const history = [...(this.configuration?.statusHistory ?? [])];

        // Get how many "empty" positions must be added to the level to have a correct alignment through the grid element
        const emptyPositionsUntilRepeatableItem = this.countEmptyPositions(history);

        // Convert the receive configuration statusHistory into a HistoryStateDisplayConfigurationModel
        const levels = this.convertHistoryToALevelsModel(history, emptyPositionsUntilRepeatableItem);

        // store levels.length to know the expanded levels length even when it is collapsed
        this.storeState({expandedLevelsSize: levels.length});

        // Add the flag for the collapsible button
        if (this.hasCollapsibleButtonWhenExpanded()) {
            this.addCollapsibleToLevel(levels[0]);
        }

        // Add the prediction path(s) if it isn't in the final state
        if (!this.isCurrentStateFinal()) {
            this.addPredictionPaths(levels);
        }

        return levels;
    }

    private addCollapsibleToLevel(level: HistoryStateDisplayConfigurationModel[]): void {
        const beforeRepeatableItemIndex = level.indexOf(level.find((entry) => entry.state.repeatable)) - 1;

        if (level[beforeRepeatableItemIndex] != null)  {
            level[beforeRepeatableItemIndex].collapsible = true;
        }
    }

    private addPredictionPaths(levels: HistoryStateDisplayConfigurationModel[][]): void {
        const lastLevelFilledStates = levels[levels.length - 1];
        const currentState = lastLevelFilledStates[lastLevelFilledStates.length - 1];

        // +1 to not include itself
        let currentStateInHappyPathIndex = this.configuration?.happyPath?.indexOf(
                this.configuration?.happyPath?.find((entry) => entry.code === currentState.state.code)) + 1;

        // if currentState is not a happy path entry
        if (currentStateInHappyPathIndex === 0 && currentState.nextStateCode != null) {
            currentStateInHappyPathIndex = this.configuration?.happyPath?.indexOf(
                    this.configuration?.happyPath?.find((entry) => entry.code === currentState.nextStateCode));
        }
        const remainingHappyPathStates = this.configuration?.happyPath?.slice(currentStateInHappyPathIndex);

        remainingHappyPathStates?.forEach((entry) => {
            levels[levels.length - 1].push({state: entry, prediction: true});
        });
    }

    private convertHistoryToALevelsModel(history: HistoryStateConfigurationModel[],
                                         emptyPositions: number): HistoryStateDisplayConfigurationModel[][] {
        // Get all the history items that are marked as repeatable (this is where the timeline has to "break")
        // If a repeatable item is the last one, it must be discarded from this array (timeline does not need to "break")
        const repeatableItems = history.filter((entry) => entry.state?.repeatable && history[history.length - 1].state !== entry.state);

        const levels: HistoryStateDisplayConfigurationModel[][] = [];
        // Reverse so we start the forEach loop from the end of the array and the next repeatable item is still available after splice
        // operation
        repeatableItems.reverse()
                .forEach((item) => {
                    const itemIndexInHistory = history.indexOf(item);
                    const repeatableItemIndexInHistory = itemIndexInHistory + 1;

                    if (!history[repeatableItemIndexInHistory]?.state.finalState) {
                        const level = history.splice(repeatableItemIndexInHistory);
                        // Add empty positions
                        for (let i = 0; i < emptyPositions; i++) {
                            level.unshift({state: {}} as HistoryStateDisplayConfigurationModel);
                        }
                        levels.push(level as HistoryStateDisplayConfigurationModel[]);
                    }
                });

        // history now matches the 1st line (from the start of the workflow until the first break)
        // If the second to last item is the initialState (i.e.: second to last item is the 1st history item), an empty position must be
        // added after it
        if (history[history.length - 2]?.state?.initialState && !this.configuration.hasCollapsibleButton) {
            history.splice(history.length - 1, 0, {state: {}} as HistoryStateDisplayConfigurationModel);
        }
        levels.push(history);

        // Reverse so we have the correct levels order (basically, undo the previous reverse)
        levels.reverse();

        return levels;
    }

    private countEmptyPositions(history: HistoryStateConfigurationModel[]): number {
        let counter;
        const entryIndex = history.indexOf(history.find((entry) => entry.state?.repeatable));
        if (entryIndex !== -1) {
            // If entryIndex = 1 it means that there's no state between the 1st position and the one that triggers the zig-zag.
            entryIndex === 1 ? counter = 1 : counter = entryIndex - 1;
        } else {
            // if entryIndex = -1 it means that didn't found any state.repeatable
            counter = 0;
        }

        return counter;
    }

    /**
     * Returns if the passed state is equal to the current state.
     * Current state is the last from the statusHistory.
     */
    private isDotAtCurrentState(state: StateConfigurationModel): boolean {
        return !state.finalState && !state.initialState && JSON.stringify(
                this.configuration.statusHistory[this.configuration.statusHistory.length - 1].state) === JSON.stringify(state);
    }

    /**
     * Returns if the last state is a repeatable state
     */
    private isLastStateRepeatable(): boolean {
        const lastState = [...this.configuration.statusHistory].pop();
        return lastState.state?.repeatable != null ? lastState.state?.repeatable : false;
    }

    private hasCollapsibleButtonWhenExpanded(): boolean {
        // If collapseAtLevel is not passed the default is the timeline to have the collapsible button when it was more than 1 level.
        const collapseLevel = this.configuration.collapseAtLevel ?? 1;

        return this.state.expandedLevelsSize > collapseLevel;
    }

    private baseStateCypressTag(levelIndex: number, index: number): string {
        return `state-level-${levelIndex}-index-${index}`;
    }
}
